import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/components/auth/AuthProvider';
import { DataProvider } from '@/contexts/DataContext';
import { LoginForm } from '@/components/auth/LoginForm';
import { Layout } from '@/components/layout/Layout';
import { DataLoader } from '@/components/common/DataLoader';
import { CreateEvaluation } from '@/components/evaluation/CreateEvaluation';
import { EvaluationRuns } from '@/components/evaluation/EvaluationRuns';
import { EvaluationDetails } from '@/components/evaluation/EvaluationDetails';
import { apiClient } from '@/services/api';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, authService } = useAuth();

  useEffect(() => {
    if (authService) {
      apiClient.setAuthService(authService);
    }
  }, [authService]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function LoginPage() {
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <LoginForm onSuccess={() => {}} />;
}

function MainPage() {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const handleEvaluationCreated = (runId: string) => {
    // Add a small delay to allow the backend to process the evaluation
    setTimeout(() => {
      setSelectedRunId(runId);
    }, 1000); // 1 second delay
  };

  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
  };

  const handleCloseDetails = () => {
    setSelectedRunId(null);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <CreateEvaluation onEvaluationCreated={handleEvaluationCreated} />
        
        <EvaluationRuns 
          onSelectRun={handleSelectRun}
          selectedRunId={selectedRunId || undefined}
        />
        
        {selectedRunId && (
          <EvaluationDetails 
            runId={selectedRunId}
            onClose={handleCloseDetails}
          />
        )}
      </div>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <DataProvider>
                  <DataLoader>
                    <MainPage />
                  </DataLoader>
                </DataProvider>
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;